import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { readVisitorRules, saveVisitorRules, VISITOR_RULE_TOPICS, type VisitorRule } from "@/api/event-about";

/**
 * The place's visitor rules, kept once and shown on every event held here.
 * Saving replaces the whole list (at most 12); it needs the profile-edit
 * permission, and the server says so when it is missing.
 */
export function VisitorRulesSection({ institutionId }: { institutionId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["institution-visitor-rules", institutionId],
    queryFn: () => readVisitorRules(institutionId),
  });
  const [draft, setDraft] = useState<VisitorRule[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const rules = draft ?? data?.data.visitorRules ?? [];

  function change(index: number, rule: Partial<VisitorRule>) {
    setDraft(rules.map((r, i) => (i === index ? { ...r, ...rule } : r)));
  }

  async function save() {
    setMsg(null);
    try {
      await saveVisitorRules(institutionId, rules);
      setDraft(null);
      setMsg("Saved.");
      await qc.invalidateQueries({ queryKey: ["institution-visitor-rules", institutionId] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "That did not work");
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
      <h2 className="text-lg font-semibold">Visitor rules ({rules.length} of 12)</h2>
      {rules.map((rule, i) => (
        <div key={i} className="flex gap-2">
          <select className="input w-40" value={rule.topic} onChange={(e) => change(i, { topic: e.target.value })}>
            {VISITOR_RULE_TOPICS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <input className="input flex-1" maxLength={200} value={rule.text} onChange={(e) => change(i, { text: e.target.value })} />
          <button className="px-2 text-sm text-red-600" onClick={() => setDraft(rules.filter((_, j) => j !== i))}>
            Remove
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <button
          className="px-3 py-1 rounded border border-gray-300 text-sm"
          disabled={rules.length >= 12}
          onClick={() => setDraft([...rules, { topic: "shoes", text: "" }])}
        >
          Add a rule
        </button>
        <button className="btn-primary" disabled={draft === null} onClick={() => void save()}>
          Save rules
        </button>
      </div>
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
    </section>
  );
}
